import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { Dispatch } from "redux";

import { showAutoOrderToast } from "@/store/autoOrderToastSlice";
import { setSessionExpired } from "@/store/sessionExpirySlice";

/**
 * Subscribe (add-to-auto-order) response handling for the users-service
 * contract (POST /users/subscriptions).
 *
 * The endpoint communicates outcome via HTTP status rather than a status-code
 * envelope in the body. Under RTK Query's `.unwrap()` only the 201 CREATED
 * outcome resolves; every other outcome (409 already-subscribed, 422
 * needs-info, 401, 400, 500) rejects with a `FetchBaseQueryError`. So the
 * caller's success path lives in the `try` (post-unwrap) and all error
 * branches live in the `catch`, classified by {@link classifySubscribeError}.
 *
 * Note: cache refresh (AUTO_ORDER_SUBSCRIPTIONS / AUTO_ORDER_POPULAR) is owned
 * by the mutation's `invalidatesTags` (see `orderApi.subscribeToProduct`), so
 * these helpers do not invalidate manually.
 */

/** Classified subscribe failure, derived from the RTK Query error's HTTP status. */
export type SubscribeErrorOutcome =
  | { kind: "INVALID_USER" }
  | { kind: "ALREADY_SUBSCRIBED" }
  | { kind: "NEEDS_INFO"; missingShipping: boolean; missingPayment: boolean; missingLicense: boolean }
  | { kind: "UNKNOWN" };

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === "object" && error != null && "status" in error;
}

/**
 * Maps a rejected `subscribeToProduct` error to a UX outcome:
 * - 401 → INVALID_USER (session expired / not logged in)
 * - 409 → ALREADY_SUBSCRIBED (product is already auto-ordered, any frequency)
 * - 422 → NEEDS_INFO, carrying the `missingShipping` / `missingPayment` flags
 *   from the response body (the contract has no license dimension)
 * - anything else (400 / 500 / network) → UNKNOWN
 */
export function classifySubscribeError(error: unknown): SubscribeErrorOutcome {
  if (isFetchBaseQueryError(error) && typeof error.status === "number") {
    switch (error.status) {
      case 401:
        return { kind: "INVALID_USER" };
      case 409:
        return { kind: "ALREADY_SUBSCRIBED" };
      case 422: {
        const body = error.data as
          | { missingShipping?: boolean; missingPayment?: boolean; missingLicense?: boolean }
          | undefined;
        return {
          kind: "NEEDS_INFO",
          missingShipping: body?.missingShipping ?? false,
          missingPayment: body?.missingPayment ?? false,
          missingLicense: body?.missingLicense ?? false,
        };
      }
    }
  }
  return { kind: "UNKNOWN" };
}

/** Success toast (201 CREATED). */
export function dispatchSubscribeSuccessToast(dispatch: Dispatch, productTitle: string | undefined): void {
  dispatch(
    showAutoOrderToast({
      toastMessage: "Added to Auto Order",
      toastSubMessage: productTitle,
      toastType: "success",
    }),
  );
}

/** Info toast for the 409 ALREADY_SUBSCRIBED outcome. */
export function dispatchAlreadySubscribedToast(dispatch: Dispatch): void {
  dispatch(
    showAutoOrderToast({
      toastMessage: "Already subscribed",
      toastSubMessage: "This product is already in your Auto Order.",
      toastType: "info",
    }),
  );
}

/** Generic catch-block toast (UNKNOWN outcome — 400 / 500 / network). */
export function dispatchSubscribeErrorToast(dispatch: Dispatch): void {
  dispatch(
    showAutoOrderToast({
      toastMessage: "Something went wrong",
      toastSubMessage: "Please try again in a moment.",
      toastType: "error",
    }),
  );
}

/**
 * Shared catch-block handler for the toast-based surfaces (popular rail +
 * dashboard `PopularProductCard`). Branches the classified error to the right UX:
 * session-expiry redirect, already-subscribed info toast, the needs-info modal
 * (via `onNeedsInfo`), or a generic error toast.
 *
 * `onAlreadySubscribed` lets the caller dismiss its add modal — "already
 * subscribed" is treated as a soft success (the modal closes).
 */
export function handleSubscribeError({
  error,
  dispatch,
  pathname,
  onNeedsInfo,
  onAlreadySubscribed,
}: {
  error: unknown;
  dispatch: Dispatch;
  pathname: string;
  onNeedsInfo: (missingShipping: boolean, missingPayment: boolean, missingLicense: boolean) => void;
  onAlreadySubscribed?: () => void;
}): void {
  const outcome = classifySubscribeError(error);
  switch (outcome.kind) {
    case "INVALID_USER":
      dispatch(setSessionExpired({ originPath: pathname }));
      return;
    case "ALREADY_SUBSCRIBED":
      dispatchAlreadySubscribedToast(dispatch);
      onAlreadySubscribed?.();
      return;
    case "NEEDS_INFO":
      onNeedsInfo(outcome.missingShipping, outcome.missingPayment, outcome.missingLicense);
      return;
    default:
      dispatchSubscribeErrorToast(dispatch);
  }
}
